import React, { useEffect, useState } from 'react'
import { globalState } from '../../../../store'
import { Form } from 'react-bootstrap'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faPlayCircle } from '@fortawesome/pro-duotone-svg-icons'
import GenericModal from '../../../../components/component-items/modal'
import GlobalStyles from '../../../../components/component-items/styles'
import Pager, { Pages } from '../../../../components/component-items/pager'
import { TableCell } from '../../../../components/component-items/datatable'
import { StatusBoxStyled } from '../../../../components/component-items/status-box'
import { notify, usePost } from '../../../../components/component-items/helpers'
import { usePermissions } from '../../../../hooks/usePermissions'
import { WaveSchedule, WaveScheduleOccurrence } from './schedule-types'
import {
  DirectiveReservationPreviewInventoryRow,
  DirectiveReservationPreviewMetrics,
  DirectivePreviewInventoryTable,
  DirectivePreviewOrderSection,
  DirectivePreviewSection,
  DirectivePreviewSummaryCards,
} from '../profiles/directive-preview-sections'
import {
  formatDateTime,
  getDisplayTimezone,
  parseDateTimeInTimezone,
  ScheduleTimeDisplayMode,
  scheduleStatusConfig,
} from './schedule-utils'

const directivePreviewPageSize = 10

const HistoryLayout = styled.div`
  .history-header {
    display: grid;
    grid-template-columns: repeat(3, minmax(150px, 1fr));
    gap: 0;
    margin-bottom: 1.05rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
    overflow: hidden;
  }

  .metric {
    position: relative;
    min-width: 0;
    padding: 0.9rem 1rem;

    &:not(:last-child) {
      border-right: 1px solid #eef2f7;
    }

    label {
      display: flex;
      gap: 0.45rem;
      align-items: center;
      color: #64748b;
      font-size: 0.7rem;
      font-weight: 800;
      letter-spacing: 0.055em;
      text-transform: uppercase;
    }

    label::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 999px;
      background: #94a3b8;
    }

    .metric-value {
      margin-top: 0.35rem;
      color: #334155;
      font-size: 0.95rem;
      font-weight: 700;
      line-height: 1.35;
      min-height: 24px;
    }

    .metric-value.strong {
      color: #0f172a;
      font-size: 1.35rem;
      line-height: 1.1;
    }

    .metric-status {
      display: flex;
      align-items: center;
      min-height: 24px;
      margin-top: 0.35rem;
    }
  }

  .filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-end;
    margin-bottom: 1rem;
  }

  .filter-field {
    min-width: 240px;
  }

  .history-table {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow-x: auto;
  }

  .history-table table {
    margin-bottom: 0;
  }

  .history-table th {
    background: #f8fafc;
    color: #475569;
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .run-time {
    display: grid;
    gap: 0.18rem;
    color: #334155;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .run-time span {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .run-status {
    display: grid;
    justify-items: center;
    gap: 0.35rem;
  }

  .run-error {
    max-width: 260px;
    color: #b42318;
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1.3;
  }

  .downstream-status {
    color: #334155;
    font-size: 0.84rem;
    font-weight: 700;
    text-transform: capitalize;
  }

  .empty-history {
    padding: 1.2rem;
    color: #64748b;
    text-align: center;
    font-size: 0.88rem;
    font-weight: 700;
  }

  .guidance {
    margin-bottom: 0.9rem;
    color: #64748b;
    font-size: 0.88rem;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(220px, 1fr));
    gap: 0.9rem;
    margin-bottom: 1rem;
  }

  .detail-grid.target-breakdown-only {
    grid-template-columns: 1fr;
    margin-bottom: 0;
  }

  .detail-card {
    padding: 1rem;
    border: 1px solid #ebedf2;
    border-radius: 8px;
    background: #ffffff;
  }

  .detail-card h5 {
    margin: 0 0 0.65rem;
    color: #334155;
    font-size: 0.95rem;
  }

  .failure-banner {
    margin-bottom: 0.85rem;
    padding: 0.78rem 0.9rem;
    border: 1px solid #fda29b;
    border-left: 4px solid #b42318;
    border-radius: 8px;
    background: #fff5f5;
    color: #7a271a;
  }

  .failure-title {
    color: #b42318;
    font-size: 0.86rem;
    font-weight: 800;
  }

  .failure-message {
    margin-top: 0.18rem;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.35;
  }

  .planned-banner {
    margin-bottom: 0.85rem;
    padding: 0.78rem 0.9rem;
    border: 1px solid #93c5fd;
    border-left: 4px solid #1d4ed8;
    border-radius: 8px;
    background: #eff6ff;
    color: #1e3a8a;
  }

  .planned-banner.blackout {
    border-color: #cbd5e1;
    border-left-color: #0f172a;
    background: #f8fafc;
    color: #0f172a;
  }

  .planned-title {
    color: inherit;
    margin-bottom: 0.28rem;
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .planned-message {
    margin-top: 0.18rem;
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.35;
  }

  .breakdown-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    background: #eff6ff;
  }

  .breakdown-summary label {
    display: block;
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
  }

  .breakdown-duration {
    color: #1d4ed8;
    font-size: 1.15rem;
    font-weight: 800;
    line-height: 1.1;
  }

  .breakdown-table-wrap {
    border-top: 1px solid #e2e8f0;
    max-height: 360px;
    overflow: auto;
  }

  .breakdown-groups {
    display: grid;
    gap: 0.65rem;
  }

  .breakdown-group {
    border: 1px solid #dbe3ef;
    border-left: 4px solid #cbd5e1;
    border-radius: 9px;
    background: #ffffff;
    overflow: hidden;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.04);
  }

  .breakdown-group.red {
    border-left-color: #dc2626;
  }

  .breakdown-group.green {
    border-left-color: #15803d;
  }

  .breakdown-group.blue {
    border-left-color: #2563eb;
  }

  .breakdown-group.silver {
    border-left-color: #64748b;
  }

  .breakdown-group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.62rem 0.8rem;
    border-bottom: 1px solid #e9eef6;
    background: #f8fafc;
  }

  .breakdown-group-heading {
    min-width: 0;
  }

  .breakdown-group-title {
    color: #0f172a;
    font-size: 0.84rem;
    font-weight: 800;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  .breakdown-group-title-row {
    display: inline-flex;
    align-items: center;
    gap: 0.42rem;
    flex-wrap: wrap;
  }

  .target-type-pill {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 0.12rem 0.46rem;
    border: 1px solid transparent;
    font-size: 0.67rem;
    font-weight: 800;
    line-height: 1.2;
    letter-spacing: 0.01em;
    text-transform: uppercase;
  }

  .target-type-pill.profile {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }

  .target-type-pill.directive {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
  }

  .breakdown-group-subtitle {
    margin-top: 0.2rem;
    color: #64748b;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: uppercase;
  }

  .breakdown-group-header-right {
    display: flex;
    align-items: center;
    gap: 0.45rem;
  }

  .group-toggle {
    border: 1px solid #bfccdf;
    border-radius: 999px;
    background: #ffffff;
    color: #334155;
    padding: 0.34rem 0.78rem;
    font-size: 0.76rem;
    font-weight: 800;
    line-height: 1;
  }

  .group-toggle:hover {
    border-color: #8fa4c5;
    background: #f8fbff;
  }

  .breakdown-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f8fafc;
  }

  .breakdown-table {
    width: 100%;
    min-width: 1020px;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .breakdown-table th {
    padding: 0.58rem 0.7rem;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 0.73rem;
    font-weight: 800;
    text-align: left;
    text-transform: uppercase;
  }

  .breakdown-table th.numeric,
  .breakdown-table td.numeric,
  .breakdown-table th.status-column,
  .breakdown-table td.status-column,
  .breakdown-table th.action-column,
  .breakdown-table td.action-column {
    text-align: center;
  }

  .breakdown-table td {
    padding: 0.62rem 0.7rem;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
    font-size: 0.88rem;
    vertical-align: middle;
  }

  .breakdown-table tr:last-child td {
    border-bottom: 0;
  }

  .directive-table-row {
    background: #ffffff;
  }

  .target-cell {
    min-width: 0;
  }

  .target-name {
    color: #334155;
    font-size: 0.92rem;
    font-weight: 700;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .target-title-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    align-items: center;
  }

  .directive-index {
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #e8f0ff;
    color: #2457b8;
    font-size: 0.64rem;
    font-weight: 800;
    flex: 0 0 auto;
    margin-top: 1px;
  }

  .target-meta {
    margin-top: 0.18rem;
    color: #475569;
    font-size: 0.86rem;
    font-weight: 700;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  .table-value {
    color: #334155;
    font-weight: 800;
  }

  .outcome-block {
    display: flex;
    justify-content: center;
    min-width: 118px;
  }

  .rerun-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    min-width: 118px;
    border: 1px solid #b7d4ff;
    border-radius: 6px;
    background: #f3f7ff;
    color: #2457b8;
    padding: 0.36rem 0.55rem;
    font-size: 0.76rem;
    font-weight: 800;
    white-space: nowrap;
  }

  .rerun-button:hover:not(:disabled) {
    border-color: #4078dc;
    background: #eaf2ff;
  }

  .rerun-button:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }

  .directive-preview-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 86px;
    border: 1px solid #c7d2e5;
    border-radius: 6px;
    background: #ffffff;
    color: #334155;
    padding: 0.36rem 0.55rem;
    font-size: 0.74rem;
    font-weight: 800;
    white-space: nowrap;
  }

  .directive-preview-button:hover:not(:disabled) {
    border-color: #8fa4c5;
    background: #f8fbff;
  }

  .directive-preview-button:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }

  .directive-focus-shell {
    margin-bottom: 0.75rem;
    border: 1px solid #dbe6f5;
    border-radius: 9px;
    background: #ffffff;
    overflow: hidden;
  }

  .directive-focus-header {
    display: grid;
    gap: 0.52rem;
    padding: 0.72rem 0.82rem;
    border-bottom: 1px solid #e8eef8;
    background: #f8fbff;
  }

  .directive-focus-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
  }

  .directive-focus-crumb {
    color: #64748b;
    font-size: 0.79rem;
    font-weight: 800;
    letter-spacing: 0.02em;
  }

  .directive-focus-title {
    color: #0f172a;
    font-size: 0.9rem;
    font-weight: 800;
    line-height: 1.25;
  }

  .directive-focus-subtitle {
    margin-top: 0.16rem;
    color: #64748b;
    font-size: 0.74rem;
    font-weight: 700;
  }

  .directive-focus-back {
    display: inline-flex;
    align-items: center;
    gap: 0.42rem;
    border: 1px solid #b8c9e6;
    border-radius: 8px;
    background: #ffffff;
    color: #1d4ed8;
    padding: 0.42rem 0.72rem;
    font-size: 0.76rem;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
  }

  .directive-focus-back:hover {
    border-color: #90b2ec;
    background: #eef4ff;
  }

  .directive-focus-body {
    padding: 0.66rem 0.78rem 0.78rem;
  }

  .directive-preview-panel {
    border: 1px solid #e3e8f2;
    border-radius: 7px;
    background: #ffffff;
    padding: 0.7rem 0.78rem;
  }

  .directive-preview-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(90px, 1fr));
    gap: 0.45rem;
  }

  .directive-preview-stat {
    border: 1px solid #e8edf6;
    border-radius: 6px;
    background: #f8fafd;
    padding: 0.42rem 0.5rem;
  }

  .directive-preview-label {
    display: block;
    color: #64748b;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1.15;
  }

  .directive-preview-value {
    display: block;
    margin-top: 0.18rem;
    color: #0f172a;
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.2;
  }

  .directive-preview-meta {
    margin-top: 0.4rem;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .directive-preview-empty {
    color: #64748b;
    font-size: 0.73rem;
    font-weight: 700;
  }

  @media (max-width: 900px) {
    .history-header {
      grid-template-columns: 1fr;
    }

    .metric:not(:last-child) {
      border-right: 0;
      border-bottom: 1px solid #eef2f7;
    }

    .detail-grid {
      grid-template-columns: 1fr;
    }

    .breakdown-table {
      min-width: 780px;
    }

    .history-table table {
      min-width: 760px;
    }

    .breakdown-table-wrap {
      overflow-x: auto;
    }
  }
`

type WaveScheduleHistoryProps = {
  open: boolean
  onClose: () => void
  schedule: WaveSchedule | null
  occurrences: WaveScheduleOccurrence[]
  loading?: boolean
  error?: boolean
  page?: number
  count?: number
  hasNext?: boolean
  hasPrevious?: boolean
  pageSize?: number
  onPageSizeChange?: (size: number) => void
  statusFilter?: string
  onStatusFilterChange?: (status: string) => void
  onPageChange?: (page: number) => void
  selectedScheduledAt?: string | null
  selectedOrderEstimateLabel?: string | null
  timeDisplayMode?: ScheduleTimeDisplayMode
  viewerTimezone?: string
  skipInFlight?: boolean
  onSkipOccurrence?: (schedule: WaveSchedule, scheduledAt: string) => Promise<void> | void
  onUnskipOccurrence?: (schedule: WaveSchedule, scheduledAt: string) => Promise<void> | void
  profileNamesById?: Record<number, string>
  directivesByProfileId?: Record<number, { id: number; name: string; expected_order_count: number | null }[]>
  directivePreviewPayloadById?: Record<number, { settings: any; sort: any; filters: any }>
  profilesLoading?: boolean
}

type LatestTargetResult = NonNullable<WaveScheduleOccurrence['target_results']>[number]
type LatestDirectiveResult = NonNullable<LatestTargetResult['directives']>[number]
type DirectiveBreakdownRow = {
  id: string
  name: string
  profile: string
  status: string
  duration: string
  order_count: number | null | undefined
  expected_order_count?: number | null
  batches_created: number | null | undefined
  directive_id: number | null
  has_run: boolean
  runId: number | null
}

type FocusedDirectiveContext = {
  groupName: string
  groupKind: 'profile' | 'directive'
  directive: DirectiveBreakdownRow
}
type BreakdownGroup = {
  id: string
  kind: 'profile' | 'directive'
  name: string
  status: string
  duration: string
  directives: DirectiveBreakdownRow[]
}

const durationToSeconds = (duration?: string) => {
  if (!duration || duration === '-') return 0
  const hours = duration.match(/(\d+)\s*h/)
  const minutes = duration.match(/(\d+)\s*m/)
  const seconds = duration.match(/(\d+)\s*s/)
  return (
    (hours ? Number(hours[1]) * 3600 : 0) + (minutes ? Number(minutes[1]) * 60 : 0) + (seconds ? Number(seconds[1]) : 0)
  )
}

const secondsToDuration = (totalSeconds: number) => {
  if (!totalSeconds) return '-'
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours) return `${hours}h ${minutes}m ${seconds}s`
  if (minutes) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

const aggregateGroupStatus = (statuses: string[]) => {
  if (!statuses.length) return 'planned'
  if (statuses.some((status) => ['failed', 'failed_to_launch'].includes(status))) return 'failed'
  if (statuses.some((status) => ['queued', 'running', 'launching'].includes(status))) return 'running'
  if (statuses.every((status) => status === 'skipped_manual')) return 'skipped_manual'
  if (statuses.every((status) => status === 'skipped_blackout')) return 'skipped_blackout'
  if (statuses.every((status) => status === 'skipped_downtime')) return 'skipped_downtime'
  if (statuses.every((status) => status.startsWith('skipped_'))) return statuses[0]
  if (statuses.every((status) => ['completed', 'launched'].includes(status))) return 'completed'
  if (statuses.every((status) => status === 'planned')) return 'planned'
  return 'pending'
}

const matchesScheduledTime = (occurrence: WaveScheduleOccurrence, selectedScheduledAt: string, timezone: string) => {
  const selectedMoment = parseDateTimeInTimezone(selectedScheduledAt, timezone)
  const selectedTime = selectedMoment?.valueOf() || Number.NaN
  if (Number.isNaN(selectedTime)) return false
  return [occurrence.scheduled_at, occurrence.scheduled_at_local].some((value) => {
    if (!value) return false
    const occurrenceMoment = parseDateTimeInTimezone(value, timezone)
    const occurrenceTime = occurrenceMoment?.valueOf() || Number.NaN
    return !Number.isNaN(occurrenceTime) && occurrenceTime === selectedTime
  })
}

export const WaveScheduleHistory = ({
  open,
  onClose,
  schedule,
  occurrences,
  loading = false,
  error = false,
  page = 1,
  count = 0,
  pageSize = 20,
  onPageSizeChange,
  statusFilter = 'all',
  onStatusFilterChange,
  onPageChange,
  selectedScheduledAt,
  selectedOrderEstimateLabel,
  timeDisplayMode = 'warehouse',
  viewerTimezone,
  skipInFlight = false,
  onSkipOccurrence,
  onUnskipOccurrence,
  profileNamesById = {},
  directivesByProfileId = {},
  directivePreviewPayloadById = {},
  profilesLoading = false,
}: WaveScheduleHistoryProps) => {
  const {
    state: { csrf },
  } = globalState()

  const { hasAccess } = usePermissions()
  const canManageSchedules = hasAccess('outbound', 'admin')
  const canRunDirectives = hasAccess('outbound', 'operator')
  const [runningDirectives, setRunningDirectives] = useState<Record<number, boolean>>({})
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [focusedDirective, setFocusedDirective] = useState<FocusedDirectiveContext | null>(null)
  const [focusedOrderPage, setFocusedOrderPage] = useState(1)
  const [showFocusedInventoryResults, setShowFocusedInventoryResults] = useState(false)
  const [showFocusedOrderResults, setShowFocusedOrderResults] = useState(true)
  const [directivePreviewStateById, setDirectivePreviewStateById] = useState<
    Record<
      number,
      {
        loading?: boolean
        error?: string | null
        metrics?: DirectiveReservationPreviewMetrics | null
        orders?: any[]
        orderCount?: number | null
        inventoryResults?: DirectiveReservationPreviewInventoryRow[]
        inventoryCount?: number | null
      }
    >
  >({})
  const totalPages = Math.max(1, Math.ceil(count / pageSize))

  useEffect(() => {
    if (!open) {
      setFocusedDirective(null)
      setShowFocusedInventoryResults(false)
      setShowFocusedOrderResults(true)
    }
  }, [open])

  if (!schedule) return null
  const displayTimezone = getDisplayTimezone(schedule.warehouse_timezone, timeDisplayMode, viewerTimezone)

  const selectedOccurrence = selectedScheduledAt
    ? occurrences.find((occurrence) => matchesScheduledTime(occurrence, selectedScheduledAt, displayTimezone)) || null
    : null
  const isLoadingSelectedSlot = Boolean(selectedScheduledAt && loading)
  const latestOccurrence =
    occurrences.find((occurrence) =>
      occurrence.target_results?.some((targetResult) => targetResult.duration || targetResult.directives?.length)
    ) ||
    occurrences[0] ||
    null
  const breakdownOccurrence = selectedScheduledAt ? selectedOccurrence : latestOccurrence
  const breakdownTitle = selectedScheduledAt
    ? `Target Breakdown for ${formatDateTime(selectedScheduledAt, true, displayTimezone)}`
    : 'Latest Target Breakdown'
  const modalHeading = selectedScheduledAt
    ? `${schedule.name} - ${breakdownTitle}`
    : `Schedule History: ${schedule.name}`
  const selectedFailureMessage =
    selectedOccurrence?.status === 'failed_to_launch'
      ? selectedOccurrence.launch_error_message || 'This schedule failed before the wave run started.'
      : selectedOccurrence?.downstream_run?.status === 'failed'
        ? 'At least one of the downstream wave runs failed.'
        : null
  const latestTargetResults: LatestTargetResult[] = breakdownOccurrence?.target_results || []
  const showSelectedEstimate = Boolean(selectedOrderEstimateLabel?.startsWith('Est.'))
  const hasRecordedBreakdown = latestTargetResults.length > 0
  const scheduleTargetById = new Map(schedule.targets.map((target) => [target.id, target]))
  const profileNameByProfileId = new Map(Object.entries(profileNamesById).map(([id, name]) => [Number(id), name]))
  const selectedWarehouseDate = selectedScheduledAt
    ? parseDateTimeInTimezone(selectedScheduledAt, schedule.warehouse_timezone)?.format('YYYY-MM-DD') || null
    : null
  const selectedDisplayMoment = selectedScheduledAt
    ? parseDateTimeInTimezone(selectedScheduledAt, displayTimezone)
    : null
  const isFutureSelectedSlot = Boolean(selectedDisplayMoment?.isValid() && selectedDisplayMoment.isAfter())
  const isSelectedBlackout = selectedOccurrence?.status === 'skipped_blackout'
  const isSelectedManualSkip = selectedOccurrence?.status === 'skipped_manual'
  const selectedBlackoutDate = selectedWarehouseDate
    ? schedule.blackout_dates.find(({ date }) => date === selectedWarehouseDate) || null
    : null
  const selectedBlackoutReason = selectedBlackoutDate?.reason?.trim() || null
  const selectedManualSkipReason =
    selectedOccurrence?.launch_error_message?.trim() ||
    selectedOccurrence?.target_results?.find((targetResult) => targetResult.note?.trim())?.note?.trim() ||
    null
  const selectedManualSkipBy = selectedOccurrence?.skipped_by?.trim() || null
  const isPlannedBlackout =
    Boolean(selectedScheduledAt) &&
    !isLoadingSelectedSlot &&
    !selectedOccurrence &&
    Boolean(selectedWarehouseDate) &&
    schedule.blackout_dates.some(({ date }) => date === selectedWarehouseDate)
  const showPlannedBanner =
    Boolean(selectedScheduledAt) &&
    !isLoadingSelectedSlot &&
    !hasRecordedBreakdown &&
    (isFutureSelectedSlot || isPlannedBlackout || isSelectedBlackout)
  const showBlackoutPlannedBanner = isSelectedBlackout || isPlannedBlackout
  const showBlackoutBanner = showPlannedBanner && showBlackoutPlannedBanner
  const showManualSkipBanner = isSelectedManualSkip
  const forcedSkipStatus = isSelectedManualSkip
    ? 'skipped_manual'
    : showBlackoutPlannedBanner
      ? 'skipped_blackout'
      : null
  const showBreakdownSummary = !showBlackoutBanner && !showManualSkipBanner
  const skipScheduledAt = selectedOccurrence?.scheduled_at || selectedScheduledAt || null
  const isSelectedLaunchTerminal =
    selectedOccurrence?.status === 'launched' ||
    selectedOccurrence?.status === 'failed_to_launch' ||
    selectedOccurrence?.status === 'launching'
  const canSkipSelectedOccurrence =
    Boolean(skipScheduledAt) &&
    isFutureSelectedSlot &&
    canManageSchedules &&
    !isPlannedBlackout &&
    !isSelectedBlackout &&
    !isSelectedManualSkip &&
    (!selectedOccurrence || !isSelectedLaunchTerminal)
  const canUnskipSelectedOccurrence =
    Boolean(skipScheduledAt) && isFutureSelectedSlot && canManageSchedules && isSelectedManualSkip

  const targetResultTone = (status: string) => {
    if (status === 'planned') return 'blue'
    if (status === 'completed' || status === 'launched') return 'green'
    if (status === 'failed' || status === 'failed_to_launch') return 'red'
    if (status === 'queued' || status === 'running' || status === 'launching') return 'blue'
    if (status.startsWith('skipped_')) return 'grey'
    if (status === 'not_launched') return 'silver'
    return 'yellow'
  }

  const occurrenceStatusTone = (status: WaveScheduleOccurrence['status']) =>
    status === 'skipped_blackout' || status === 'skipped_manual'
      ? 'grey'
      : scheduleStatusConfig[status]?.tone || 'silver'

  const targetResultLabel = (status: string) => {
    if (status === 'planned') return 'Planned'
    if (status === 'completed') return 'Completed'
    if (status === 'launched') return 'Started'
    if (status === 'failed' || status === 'failed_to_launch') return 'Failed'
    if (status === 'queued') return 'Queued'
    if (status === 'running' || status === 'launching') return 'Running'
    if (status === 'skipped_blackout') return 'Blackout'
    if (status === 'skipped_downtime') return 'Downtime'
    if (status === 'skipped_manual') return 'Manual Skip'
    if (status === 'not_launched') return 'Not Launched'
    return 'Pending'
  }
  const skipOccurrence = async () => {
    if (!skipScheduledAt || !onSkipOccurrence) return
    if (!confirm('Skip this scheduled occurrence?')) return
    await onSkipOccurrence(schedule, skipScheduledAt)
  }
  const unskipOccurrence = async () => {
    if (!skipScheduledAt || !onUnskipOccurrence) return
    if (!confirm('Unskip this occurrence and allow it to run?')) return
    await onUnskipOccurrence(schedule, skipScheduledAt)
  }
  const modalButtons = (
    <>
      {canUnskipSelectedOccurrence ? (
        <GlobalStyles.Button className="secondary" type="button" onClick={unskipOccurrence} disabled={skipInFlight}>
          Unskip
        </GlobalStyles.Button>
      ) : null}
      {canSkipSelectedOccurrence ? (
        <GlobalStyles.Button className="secondary" type="button" onClick={skipOccurrence} disabled={skipInFlight}>
          Skip
        </GlobalStyles.Button>
      ) : null}
      <GlobalStyles.Button type="button" onClick={onClose}>
        Close
      </GlobalStyles.Button>
    </>
  )
  const directiveRunId = (directive: LatestDirectiveResult, parentTargetId: string) => {
    const parentTarget = scheduleTargetById.get(parentTargetId)
    if (parentTarget?.target_type === 'wave_directive' && parentTarget.wave_directive_id) {
      return parentTarget.wave_directive_id
    }

    const directId = Number(directive.id)
    if (Number.isFinite(directId)) return directId

    const numericId = String(directive.id).match(/(\d+)$/)
    return numericId ? Number(numericId[1]) : null
  }
  const expectedOrdersForDirective = (directiveId: number | null | undefined, profileId?: number | null) => {
    if (!directiveId) return null
    if (profileId) {
      const fromProfile = (directivesByProfileId[profileId] || []).find((directive) => directive.id === directiveId)
      if (fromProfile?.expected_order_count != null) return fromProfile.expected_order_count
    }
    for (const directives of Object.values(directivesByProfileId)) {
      const match = directives.find((directive) => directive.id === directiveId)
      if (match?.expected_order_count != null) return match.expected_order_count
    }
    return null
  }
  const parseDirectivePreviewMetrics = (response: any): DirectiveReservationPreviewMetrics | null => {
    const reservationPreview = response?.reservation_preview || response?.reservationPreview || null
    if (!reservationPreview) return null
    return {
      selected:
        reservationPreview.selected == null || Number.isNaN(Number(reservationPreview.selected))
          ? null
          : Number(reservationPreview.selected),
      reservable:
        reservationPreview.reservable == null || Number.isNaN(Number(reservationPreview.reservable))
          ? null
          : Number(reservationPreview.reservable),
      not_reservable:
        reservationPreview.not_reservable == null || Number.isNaN(Number(reservationPreview.not_reservable))
          ? null
          : Number(reservationPreview.not_reservable),
      units_reserved:
        reservationPreview.units_reserved == null || Number.isNaN(Number(reservationPreview.units_reserved))
          ? null
          : Number(reservationPreview.units_reserved),
      product_variants:
        reservationPreview.product_variants == null || Number.isNaN(Number(reservationPreview.product_variants))
          ? null
          : Number(reservationPreview.product_variants),
    }
  }
  const loadDirectivePreview = async (directiveId: number, orderPage = 1) => {
    const payload = directivePreviewPayloadById[directiveId]
    if (!payload) {
      setDirectivePreviewStateById((current) => ({
        ...current,
        [directiveId]: { loading: false, error: 'Preview configuration unavailable.', metrics: null },
      }))
      return
    }

    setDirectivePreviewStateById((current) => ({
      ...current,
      [directiveId]: { ...current[directiveId], loading: true, error: null },
    }))

    try {
      const response = await usePost(
        '/api/inventory/wave-directive/preview_directive/',
        {
          settings: payload.settings,
          sort: payload.sort,
          filters: payload.filters,
          preview: true,
          pagination: { page_size: directivePreviewPageSize, inventory_page: 1, order_page: orderPage },
        },
        csrf,
        false
      )

      if (!response || response.error) {
        setDirectivePreviewStateById((current) => ({
          ...current,
          [directiveId]: {
            loading: false,
            error: response?.error || 'Unable to load directive preview.',
            metrics: null,
          },
        }))
        return
      }

      setDirectivePreviewStateById((current) => ({
        ...current,
        [directiveId]: {
          loading: false,
          error: null,
          metrics: parseDirectivePreviewMetrics(response),
          orders: Array.isArray(response?.orders) ? response.orders : [],
          orderCount: Number.isFinite(Number(response?.filtered)) ? Number(response.filtered) : null,
          inventoryResults: Array.isArray(response?.reservation_preview?.inventory_results)
            ? response.reservation_preview.inventory_results
            : Array.isArray(response?.reservationPreview?.inventoryResults)
              ? response.reservationPreview.inventoryResults
              : [],
          inventoryCount: Number.isFinite(Number(response?.reservation_preview?.inventory_results_total))
            ? Number(response.reservation_preview.inventory_results_total)
            : Number.isFinite(Number(response?.reservationPreview?.inventoryResultsTotal))
              ? Number(response.reservationPreview.inventoryResultsTotal)
              : null,
        },
      }))
    } catch (error) {
      setDirectivePreviewStateById((current) => ({
        ...current,
        [directiveId]: {
          loading: false,
          error: 'Unable to load directive preview.',
          metrics: null,
        },
      }))
    }
  }
  const openDirectiveFocus = (context: FocusedDirectiveContext) => {
    setFocusedDirective(context)
    setFocusedOrderPage(1)
    setShowFocusedInventoryResults(false)
    setShowFocusedOrderResults(true)
    const directiveId = context.directive.directive_id
    if (!directiveId) return
    const previewState = directivePreviewStateById[directiveId]
    if (!previewState?.metrics && !previewState?.loading) {
      void loadDirectivePreview(directiveId)
    }
  }
  const runDirective = (directiveId: number, directiveName: string) => {
    if (!confirm(`Run directive "${directiveName}" now?`)) return

    setRunningDirectives((current) => ({ ...current, [directiveId]: true }))
    usePost(`/api/inventory/wave-directive/${directiveId}/run_directive/`, {}, csrf, false)
      .then((response) => {
        if (!response) return
        if (response.error) {
          notify({ type: 'warning', message: response.error })
        } else {
          notify({ type: 'success', message: 'Directive run started' })
        }
      })
      .finally(() => {
        setRunningDirectives((current) => {
          const next = { ...current }
          delete next[directiveId]
          return next
        })
      })
  }
  const breakdownGroups: BreakdownGroup[] = hasRecordedBreakdown
    ? (latestTargetResults
        .map((targetResult) => {
          const scheduleTarget = scheduleTargetById.get(targetResult.target_id)
          const directiveRows: DirectiveBreakdownRow[] = targetResult.directives?.length
            ? targetResult.directives.map((directive) => ({
                id: `${targetResult.target_id}-${directive.id}`,
                name: directive.name,
                profile: directive.parent_profile_name || '',
                status: forcedSkipStatus || directive.status,
                duration: directive.duration || '-',
                order_count: directive.order_count,
                expected_order_count: expectedOrdersForDirective(Number(directive.id) || null),
                batches_created: directive.batches_created,
                directive_id: Number.isFinite(Number(directive.id)) ? Number(directive.id) : null,
                has_run: directive.run_id != null,
                runId: directiveRunId(directive, targetResult.target_id),
              }))
            : []

          if (!directiveRows.length && scheduleTarget?.target_type === 'wave_directive') {
            directiveRows.push({
              id: `fallback-${targetResult.target_id}`,
              name: targetResult.label,
              profile: targetResult.secondary_label || '',
              status: forcedSkipStatus || targetResult.status,
              duration: targetResult.duration || '-',
              order_count: targetResult.order_count,
              expected_order_count: expectedOrdersForDirective(
                scheduleTarget.wave_directive_id,
                scheduleTarget.parent_profile_id
              ),
              batches_created: targetResult.batches_created,
              directive_id: scheduleTarget.wave_directive_id || null,
              has_run: Boolean(targetResult.downstream_run?.id),
              runId: scheduleTarget.wave_directive_id || null,
            })
          }

          if (!directiveRows.length) {
            directiveRows.push({
              id: `fallback-${targetResult.target_id}`,
              name: targetResult.label,
              profile: targetResult.secondary_label || '',
              status: forcedSkipStatus || targetResult.status,
              duration: targetResult.duration || '-',
              order_count: targetResult.order_count,
              expected_order_count: null,
              batches_created: targetResult.batches_created,
              directive_id: null,
              has_run: false,
              runId: null,
            })
          }

          const totalDurationSeconds = directiveRows.reduce((total, row) => total + durationToSeconds(row.duration), 0)
          const directiveStatuses = directiveRows.map((row) => row.status)
          const isProfileGroup = scheduleTarget?.target_type === 'wave_profile'

          return {
            id: targetResult.target_id,
            kind: isProfileGroup ? 'profile' : 'directive',
            name: targetResult.label,
            status: aggregateGroupStatus(directiveStatuses),
            duration: secondsToDuration(totalDurationSeconds),
            directives: directiveRows,
          }
        })
        .filter(Boolean) as BreakdownGroup[])
    : showPlannedBanner
      ? schedule.targets
          .slice()
          .sort((left, right) => left.execution_order - right.execution_order)
          .map((target) => {
            if (target.target_type === 'wave_profile' && target.wave_profile_id) {
              const directives = directivesByProfileId[target.wave_profile_id] || []
              const directiveRows = directives.length
                ? directives.map((directive) => ({
                    id: `planned-${target.id}-${directive.id}`,
                    name: directive.name,
                    profile: target.label,
                    status: showBlackoutPlannedBanner ? 'skipped_blackout' : 'planned',
                    duration: '-',
                    order_count: null,
                    expected_order_count: directive.expected_order_count,
                    batches_created: null,
                    directive_id: directive.id,
                    has_run: false,
                    runId: null,
                  }))
                : [
                    {
                      id: `planned-${target.id}-loading`,
                      name: profilesLoading ? 'Loading directives...' : 'No directives configured',
                      profile: target.label,
                      status: showBlackoutPlannedBanner ? 'skipped_blackout' : 'planned',
                      duration: '-',
                      order_count: null,
                      expected_order_count: null,
                      batches_created: null,
                      directive_id: null,
                      has_run: false,
                      runId: null,
                    },
                  ]
              return {
                id: `planned-${target.id}`,
                kind: 'profile' as const,
                name: target.label,
                status: showBlackoutPlannedBanner ? 'skipped_blackout' : 'planned',
                duration: '-',
                directives: directiveRows,
              }
            }

            return {
              id: `planned-${target.id}`,
              kind: 'directive' as const,
              name: target.label,
              status: showBlackoutPlannedBanner ? 'skipped_blackout' : 'planned',
              duration: '-',
              directives: [
                {
                  id: `planned-${target.id}-directive`,
                  name: target.label,
                  profile: target.parent_profile_id ? profileNameByProfileId.get(target.parent_profile_id) || '' : '',
                  status: showBlackoutPlannedBanner ? 'skipped_blackout' : 'planned',
                  duration: '-',
                  order_count: null,
                  expected_order_count: expectedOrdersForDirective(target.wave_directive_id, target.parent_profile_id),
                  batches_created: null,
                  directive_id: target.wave_directive_id || null,
                  has_run: false,
                  runId: null,
                },
              ],
            }
          })
      : []
  const totalBreakdownDuration = secondsToDuration(
    breakdownGroups.reduce((total, group) => total + durationToSeconds(group.duration), 0)
  )
  const focusedPreviewState = focusedDirective?.directive.directive_id
    ? directivePreviewStateById[focusedDirective.directive.directive_id]
    : null

  return (
    <>
      <GenericModal heading={modalHeading} show={open} size="xl" onHide={onClose} buttons={modalButtons}>
        <HistoryLayout>
          {selectedScheduledAt && (
            <div className="detail-grid target-breakdown-only">
              <div className="detail-card">
                {selectedFailureMessage ? (
                  <div className="failure-banner">
                    <div className="failure-title">Schedule failed</div>
                    <div className="failure-message">{selectedFailureMessage}</div>
                  </div>
                ) : null}
                {showBlackoutBanner ? (
                  <div className={`planned-banner${showBlackoutPlannedBanner ? ' blackout' : ''}`}>
                    <div className="planned-title">Skipped by blackout date</div>
                    <div className="planned-message">
                      <div>
                        {isSelectedBlackout
                          ? 'This occurrence was skipped because of a configured blackout date. The table below shows what would have run.'
                          : 'This slot is blacked out and will be skipped. The table below shows what would have run.'}
                      </div>
                      {selectedBlackoutReason ? <div>Reason: {selectedBlackoutReason}</div> : null}
                    </div>
                  </div>
                ) : null}
                {showManualSkipBanner ? (
                  <div className="planned-banner blackout">
                    <div className="planned-title">Skipped manually</div>
                    <div className="planned-message">
                      <div>
                        This occurrence was manually skipped by {selectedManualSkipBy || 'an operator'}. Use Unskip to
                        restore it.
                      </div>
                      {selectedManualSkipReason ? <div>Reason: {selectedManualSkipReason}</div> : null}
                    </div>
                  </div>
                ) : null}
                {isLoadingSelectedSlot ? (
                  <div className="empty-history">Loading selected slot details...</div>
                ) : !breakdownGroups.length ? (
                  <div className="empty-history">No target breakdown data is available for this occurrence yet.</div>
                ) : null}
                {breakdownGroups.length ? (
                  <>
                    {showBreakdownSummary ? (
                      <div className="breakdown-summary">
                        <div>
                          <label>
                            {showSelectedEstimate
                              ? 'Estimated Orders'
                              : hasRecordedBreakdown
                                ? 'Total Run Time'
                                : 'Expected Plan'}
                          </label>
                          <div className="breakdown-duration">
                            {showSelectedEstimate
                              ? selectedOrderEstimateLabel
                              : hasRecordedBreakdown
                                ? totalBreakdownDuration
                                : 'Awaiting execution'}
                          </div>
                        </div>
                        <span style={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 700 }}>
                          {breakdownGroups.reduce((total, group) => total + group.directives.length, 0)}
                          {breakdownGroups.reduce((total, group) => total + group.directives.length, 0) === 1
                            ? ' directive'
                            : ' directives'}
                        </span>
                      </div>
                    ) : null}
                    {focusedDirective ? (
                      <div className="directive-focus-shell">
                        <div className="directive-focus-header">
                          <div className="directive-focus-topline">
                            <div className="directive-focus-crumb">Directive Preview</div>
                            <button
                              type="button"
                              className="directive-focus-back"
                              onClick={() => setFocusedDirective(null)}
                            >
                              <FontAwesomeIcon icon={faArrowLeft} />
                              Back to Target Breakdown
                            </button>
                          </div>
                          <div style={{ display: 'inline-flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                            <div>
                              <div className="directive-focus-title">{focusedDirective.directive.name}</div>
                              <div className="directive-focus-subtitle">
                                {focusedDirective.groupKind === 'profile'
                                  ? `${focusedDirective.groupName} · Profile target`
                                  : `${focusedDirective.directive.profile || focusedDirective.groupName} · Directive target`}
                              </div>
                            </div>
                            <StatusBoxStyled className={targetResultTone(focusedDirective.directive.status)}>
                              {targetResultLabel(focusedDirective.directive.status)}
                            </StatusBoxStyled>
                          </div>
                        </div>
                        <div className="directive-focus-body">
                          {focusedPreviewState?.error ? (
                            <div className="directive-preview-meta">{focusedPreviewState.error}</div>
                          ) : focusedPreviewState?.metrics ? (
                            <>
                              <div className="directive-preview-panel">
                                <DirectivePreviewSummaryCards metrics={focusedPreviewState.metrics} />
                              </div>
                              <DirectivePreviewSection
                                title="Inventory Results"
                                count={focusedPreviewState.inventoryCount}
                                open={showFocusedInventoryResults}
                                setOpen={setShowFocusedInventoryResults}
                              >
                                <DirectivePreviewInventoryTable rows={focusedPreviewState.inventoryResults || []} />
                              </DirectivePreviewSection>
                              <DirectivePreviewOrderSection
                                orders={focusedPreviewState.orders || []}
                                count={focusedPreviewState.orderCount || 0}
                                open={showFocusedOrderResults}
                                setOpen={setShowFocusedOrderResults}
                                page={focusedOrderPage}
                                setPage={(nextPage: number) => {
                                  setFocusedOrderPage(nextPage)
                                  if (focusedDirective.directive.directive_id) {
                                    void loadDirectivePreview(focusedDirective.directive.directive_id, nextPage)
                                  }
                                }}
                                pageSize={directivePreviewPageSize}
                                loading={Boolean(focusedPreviewState.loading)}
                              />
                            </>
                          ) : (
                            <div className="directive-preview-meta">Loading directive preview...</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="breakdown-groups">
                        {breakdownGroups.map((group) => {
                          const defaultExpanded = group.directives.some(
                            (directive) => directive.status === 'failed' || directive.status === 'failed_to_launch'
                          )
                          const isExpanded = expandedGroups[group.id] ?? defaultExpanded
                          const groupTone = targetResultTone(group.status)
                          return (
                            <div className={`breakdown-group ${groupTone}`} key={group.id}>
                              <div className="breakdown-group-header">
                                <div className="breakdown-group-heading">
                                  <div className="breakdown-group-title-row">
                                    <div className="breakdown-group-title">{group.name}</div>
                                    <span
                                      className={`target-type-pill ${group.kind === 'profile' ? 'profile' : 'directive'}`}
                                    >
                                      {group.kind === 'profile' ? 'Profile' : 'Directive'}
                                    </span>
                                  </div>
                                  <div className="breakdown-group-subtitle">
                                    {group.directives.length} directive{group.directives.length === 1 ? '' : 's'}
                                  </div>
                                </div>
                                <div className="breakdown-group-header-right">
                                  <StatusBoxStyled className={targetResultTone(group.status)}>
                                    {targetResultLabel(group.status)}
                                  </StatusBoxStyled>
                                  <button
                                    type="button"
                                    className="group-toggle"
                                    onClick={() =>
                                      setExpandedGroups((current) => ({
                                        ...current,
                                        [group.id]: !(current[group.id] ?? defaultExpanded),
                                      }))
                                    }
                                  >
                                    {isExpanded ? 'Collapse' : 'Expand'}
                                  </button>
                                </div>
                              </div>
                              {isExpanded ? (
                                <div className="breakdown-table-wrap">
                                  <table className="breakdown-table">
                                    <colgroup>
                                      <col style={{ width: group.kind === 'profile' ? '500px' : '280px' }} />
                                      {group.kind === 'directive' ? <col style={{ width: '220px' }} /> : null}
                                      <col style={{ width: '86px' }} />
                                      <col style={{ width: '86px' }} />
                                      <col style={{ width: '98px' }} />
                                      <col style={{ width: '126px' }} />
                                      <col style={{ width: '148px' }} />
                                    </colgroup>
                                    <thead>
                                      <tr>
                                        <th>Directive</th>
                                        {group.kind === 'directive' ? <th>Parent Profile</th> : null}
                                        <th className="numeric">Orders</th>
                                        <th className="numeric">Batches</th>
                                        <th className="numeric">Duration</th>
                                        <th className="status-column">Status</th>
                                        <th className="action-column">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {group.directives.map((directive, directiveIndex) => {
                                        const showRunAction =
                                          canRunDirectives &&
                                          (directive.status === 'failed' || directive.status === 'failed_to_launch')
                                        const previewState = directive.directive_id
                                          ? directivePreviewStateById[directive.directive_id]
                                          : undefined
                                        const displayedOrderCount =
                                          directive.order_count ??
                                          (showSelectedEstimate ? directive.expected_order_count : null)
                                        return (
                                          <tr className="directive-table-row" key={directive.id}>
                                            <td>
                                              <div className="target-cell">
                                                <div className="target-title-row">
                                                  <span className="directive-index">{directiveIndex + 1}</span>
                                                  <div className="target-name">{directive.name}</div>
                                                </div>
                                              </div>
                                            </td>
                                            {group.kind === 'directive' ? (
                                              <td>
                                                <div className="target-meta">{directive.profile || '-'}</div>
                                              </td>
                                            ) : null}
                                            <td className="numeric">
                                              <span className="table-value">
                                                {displayedOrderCount == null
                                                  ? '-'
                                                  : directive.order_count == null
                                                    ? `Est. ${displayedOrderCount}`
                                                    : displayedOrderCount}
                                              </span>
                                            </td>
                                            <td className="numeric">
                                              <span className="table-value">{directive.batches_created ?? '-'}</span>
                                            </td>
                                            <td className="numeric">
                                              <span className="table-value">{directive.duration}</span>
                                            </td>
                                            <td className="status-column">
                                              <div className="outcome-block">
                                                <StatusBoxStyled className={targetResultTone(directive.status)}>
                                                  {targetResultLabel(directive.status)}
                                                </StatusBoxStyled>
                                              </div>
                                            </td>
                                            <td className="action-column">
                                              <div
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  gap: '0.38rem',
                                                  flexWrap: 'wrap',
                                                }}
                                              >
                                                {directive.directive_id && !directive.has_run ? (
                                                  <button
                                                    className="directive-preview-button"
                                                    type="button"
                                                    onClick={() =>
                                                      openDirectiveFocus({
                                                        groupName: group.name,
                                                        groupKind: group.kind,
                                                        directive,
                                                      })
                                                    }
                                                    disabled={Boolean(previewState?.loading)}
                                                  >
                                                    {previewState?.loading ? 'Loading...' : 'View Preview'}
                                                  </button>
                                                ) : null}
                                                {showRunAction ? (
                                                  <button
                                                    className="rerun-button"
                                                    type="button"
                                                    onClick={() =>
                                                      directive.runId && runDirective(directive.runId, directive.name)
                                                    }
                                                    disabled={!directive.runId || runningDirectives[directive.runId]}
                                                  >
                                                    <FontAwesomeIcon icon={faPlayCircle} />
                                                    {directive.runId && runningDirectives[directive.runId]
                                                      ? 'Running'
                                                      : 'Run Directive'}
                                                  </button>
                                                ) : null}
                                                {!showRunAction && !directive.directive_id ? '-' : null}
                                              </div>
                                            </td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : null}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          )}

          {!selectedScheduledAt && (
            <>
              <div className="filter-row">
                <div className="filter-field">
                  <Form.Select
                    aria-label="Filter by launch status"
                    value={statusFilter}
                    onChange={(event) => onStatusFilterChange?.(event.target.value)}
                  >
                    <option value="all">All statuses</option>
                    {Object.entries(scheduleStatusConfig).map(([value, config]) => (
                      <option value={value} key={value}>
                        {config.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

              <div className="history-table">
                <GlobalStyles.DataTable>
                  <thead>
                    <tr>
                      <th>
                        <span className="text center">Scheduled</span>
                      </th>
                      <th>
                        <span className="text center">Launched</span>
                      </th>
                      <th>
                        <span className="text center">Status</span>
                      </th>
                      <th>
                        <span className="text center">Wave Run Status</span>
                      </th>
                      <th>
                        <span className="text center">Attempts</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {occurrences.length ? (
                      occurrences.map((occurrence) => (
                        <GlobalStyles.TableRow key={occurrence.id}>
                          <TableCell center>
                            <div className="run-time">
                              {formatDateTime(occurrence.scheduled_at, true, displayTimezone)}
                              <span>Scheduled time</span>
                            </div>
                          </TableCell>
                          <TableCell center>
                            <div className="run-time">
                              {occurrence.launched_at
                                ? formatDateTime(occurrence.launched_at, false, displayTimezone)
                                : '-'}
                              <span>{occurrence.launched_at ? 'Launch time' : 'Not launched'}</span>
                            </div>
                          </TableCell>
                          <TableCell center>
                            <div className="run-status">
                              <StatusBoxStyled className={occurrenceStatusTone(occurrence.status)}>
                                {scheduleStatusConfig[occurrence.status]?.label || occurrence.status}
                              </StatusBoxStyled>
                              {occurrence.launch_error_message ? (
                                <div className="run-error">{occurrence.launch_error_message}</div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell center>
                            <span className="downstream-status">
                              {occurrence.downstream_run?.status || 'Not launched'}
                            </span>
                          </TableCell>
                          <TableCell center>{occurrence.attempt_count}</TableCell>
                        </GlobalStyles.TableRow>
                      ))
                    ) : (
                      <GlobalStyles.TableRow>
                        <TableCell colspan={5}>
                          <div className="empty-history">
                            {loading
                              ? 'Loading schedule history...'
                              : error
                                ? 'Schedule history could not be loaded. Refresh the page or try again.'
                                : 'No schedule runs match this filter.'}
                          </div>
                        </TableCell>
                      </GlobalStyles.TableRow>
                    )}
                  </tbody>
                </GlobalStyles.DataTable>
              </div>
              {!loading && !error && count > 0 ? (
                <GlobalStyles.CardFooter>
                  <Pages
                    page={page}
                    setPage={(nextPage: number) => onPageChange?.(nextPage)}
                    total={totalPages}
                    show={5}
                  />
                  <Pager
                    options={[10, 25, 50, 100]}
                    page={page}
                    total={count}
                    rows={pageSize}
                    setRows={(rows: number) => onPageSizeChange?.(rows)}
                  />
                </GlobalStyles.CardFooter>
              ) : null}
            </>
          )}
        </HistoryLayout>
      </GenericModal>
    </>
  )
}
